"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { INITIAL_LOCALE, isLocale, localeDir, normalizeLocale, PREFIXED_LOCALES, type Locale } from "./translations";
// Types only: the copy itself arrives per language as the `messages` prop.
import type { translations } from "./translations";
import type { Messages } from "./messages";

type Ctx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: <S extends keyof typeof translations, K extends keyof (typeof translations)[S]>(
    section: S,
    key: K,
  ) => string;
  /** Untyped variant for non-string values such as arrays (faq.items). */
  tAny: <S extends keyof typeof translations, K extends keyof (typeof translations)[S]>(
    section: S,
    key: K,
  ) => unknown;
};

const LanguageContext = createContext<Ctx | null>(null);

const STORAGE_KEY = "dw.locale";
/** Explicit choice marker, see LOCALE_CHOICE_COOKIE in src/proxy.ts. */
const CHOICE_KEY = "dw.locale.choice";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Resolves the client-side locale. Source order is significant for SEO and must
 * not be reordered: cookie `dw.locale` -> URL prefix -> localStorage ->
 * navigator.language -> INITIAL_LOCALE.
 *
 * This is the display default only. The German fallback for missing
 * translations is already part of `messages` (see messagesFor).
 */
function resolveInitialLocale(): Locale {
  if (typeof window === "undefined") return INITIAL_LOCALE;

  // Cookie wins: set by the middleware for /en/ and /da/ URLs, or by an explicit
  // user choice via setLocale().
  const fromCookie = readCookie(STORAGE_KEY);
  if (isLocale(fromCookie)) return fromCookie;

  // URL prefix is the safety net while the cookie has not been written yet.
  const path = window.location.pathname;
  const prefixed = PREFIXED_LOCALES.find((l) => path === `/${l}` || path.startsWith(`/${l}/`));
  if (prefixed) return prefixed;

  // localStorage holds an older explicit user choice.
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;

  return normalizeLocale(window.navigator.language?.slice(0, 2), INITIAL_LOCALE);
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
}

export function LanguageProvider({
  children,
  initialLocale,
  messages,
}: {
  children: ReactNode;
  /**
   * Server-resolved locale (cookie or Accept-Language). Required for
   * hydration-safe first paint: without it the client corrects the locale
   * after mount, which flashes the wrong language.
   */
  initialLocale?: Locale;
  /**
   * The copy of `initialLocale` only, from messagesFor() on the server. All
   * seven languages in the browser bundle would make every page much heavier.
   */
  messages?: Messages;
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? INITIAL_LOCALE);

  useEffect(() => {
    // A11y: `<html lang>` must match the rendered locale. A nested provider (portal)
    // can override the root locale with the customer's DB language, so the root
    // layout's `lang` attribute has to be corrected client-side.
    if (typeof document !== "undefined") {
      if (document.documentElement.lang !== locale) document.documentElement.lang = locale;
      // Arabic reads right to left; the root layout sets dir on the server,
      // this keeps it right when a nested provider (portal) changes the locale.
      const dir = localeDir(locale);
      if (document.documentElement.dir !== dir) document.documentElement.dir = dir;
    }
  }, [locale]);

  useEffect(() => {
    // A server-resolved locale is authoritative; only correct client-side when it
    // is missing (static prerender or stale cache).
    if (initialLocale) return;

    const l = resolveInitialLocale();
    if (typeof document !== "undefined") {
      document.documentElement.lang = l;
    }
    // Set synchronously: a deferred update can be cancelled by Strict Mode
    // remounts before it lands.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocaleState(l);
  }, [initialLocale]);

  // Stores the choice only. The page holds the copy of one language, so the
  // caller (the language switcher) loads the page again, and the server
  // renders it in the new language; switching the state here would show a
  // mix of both languages until then.
  const setLocale = useCallback((l: Locale) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, l);
      writeCookie(STORAGE_KEY, l);
      // Only the language switcher calls setLocale: an explicit choice, which
      // outranks the German default for DE/AT/CH in the proxy.
      writeCookie(CHOICE_KEY, l);
    }
  }, []);

  const value = useMemo<Ctx>(() => {
    const t: Ctx["t"] = (section, key) => {
      const v = messages?.[section as string]?.[key as string];
      return typeof v === "string" ? v : "";
    };
    const tAny: Ctx["tAny"] = (section, key) => messages?.[section as string]?.[key as string] ?? null;
    return { locale, setLocale, t, tAny };
  }, [locale, setLocale, messages]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Components may render outside a provider (e.g. admin); return a stable
    // no-op context instead of throwing.
    return {
      locale: INITIAL_LOCALE,
      setLocale: () => {},
      t: () => "",
      tAny: () => null,
    } as Ctx;
  }
  return ctx;
}
